import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../redux/store';
import { LoginResponseData } from '../../../feature-module/auth/login/api/loginApi';

interface AuthState {
    token: string | null;
    user: LoginResponseData['user'] | null;
    isLoading: boolean;
    isError: boolean;
    message: string;
    isAuthenticated: boolean;
}

const initialState: AuthState = {
    token: localStorage.getItem('token'),
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    isLoading: false,
    isError: false,
    message: '',
    isAuthenticated: localStorage.getItem('isAuthenticated') === 'true'
};

export const checkTokenValidity = createAsyncThunk(
    'auth/checkToken',
    async (_, { getState }) => {
        const { token, user } = (getState() as RootState).authSlice;
        
        const isValid = !!token && !!user?.role;
        return isValid;
    }
);

const authSlice = createSlice({
    name: 'authSlice',
    initialState,
    reducers: {
        setCredentials: (state, action: PayloadAction<LoginResponseData>) => {
            state.token = action.payload.token;
            state.user = action.payload.user;
            state.isAuthenticated = true;
            
            // Store all auth data in localStorage
            localStorage.setItem('token', action.payload.token);
            localStorage.setItem('user', JSON.stringify(action.payload.user));
            localStorage.setItem('isAuthenticated', 'true');
        },
        logout: (state) => {
            state.token = null;
            state.user = null;
            state.isAuthenticated = false;
            
            // Clear all auth data from localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
        },
        reset: (state) => {
            state.isLoading = false;
            state.isError = false;
            state.message = '';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(checkTokenValidity.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(checkTokenValidity.fulfilled, (state, action) => {
                state.isLoading = false;
                // Sync with localStorage on validation
                state.isAuthenticated = action.payload;
                localStorage.setItem('isAuthenticated', action.payload.toString());
            })
            .addCase(checkTokenValidity.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.error.message || 'Token validation failed';
                state.isAuthenticated = false;
                localStorage.setItem('isAuthenticated', 'false');
            });
    }
});

export const { setCredentials, logout, reset } = authSlice.actions;

export const selectUserRole = (state: RootState) => state.authSlice.user?.role;
export const selectCurrentUser = (state: RootState) => state.authSlice.user;
export const selectIsAuthenticated = (state: RootState) => 
    state.authSlice.isAuthenticated && !!state.authSlice.user?.role;

export const selectCurrentToken = (state: RootState) => state.authSlice.token;

export default authSlice.reducer;
